import { pascalCase } from 'change-case';

import { type ColumnDataType, type ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import { type PredefinedOperator } from '@blaze-shared/datasets-domain';
import { type RuntimeEventType } from '@blaze-shared/schema-domain';
import { isServerOnlyWorkflow, type Workflow } from '@blaze-shared/workflow-domain';

import { type CanvasElementId } from '../canvas-element/canvas-element-id';
import { type WrapperRefIds } from '../canvas-element/wrapper';

/**
 * 'search', 'match', and 'ranged' are used for the Search & Filters feature
 * 'parameter' is used for fields generated as the User Input for the API Call as Data Source feature
 * 'confirm' is a confirm button used to trigger the workflow
 */
export type WrapperFilterType = 'search' | 'match' | 'parameter' | 'confirm' | 'ranged';

export const WORKFLOW_GENERATED_DATA_FILTER_INPUT_NAME_PREFIX = 'dataFilter';
export const WORKFLOW_GENERATED_INPUT_NAME_PREFIX = 'filter';
export const WORKFLOW_GENERATED_INPUT_PARAMETER_NAME_PREFIX = 'parameter';
export const SEARCH_AND_FILTERS_CONDITION_PREFIX = 'searchAndFilters_';
export const AUTO_QUERY_LOGICAL_NODE_ID = '■AUTOMATIC_FILTERS';
export const CUSTOM_QUERY_LOGICAL_NODE_ID = '■CUSTOM_FILTERS';

export const isSearchAndFiltersCondition = (condition?: {
  conditionId?: string;
}): boolean | undefined => {
  return condition?.conditionId?.startsWith?.(SEARCH_AND_FILTERS_CONDITION_PREFIX);
};

export const getWorkflowInputNameForGeneratedField = (
  data: Pick<WrapperConnectionData, 'filterType' | 'fieldName'>,
): string | undefined => {
  switch (data.filterType) {
    case 'search':
      return `${WORKFLOW_GENERATED_INPUT_NAME_PREFIX}Search`;
    case 'match':
      if (!data.fieldName) {
        return;
      }
      return `${WORKFLOW_GENERATED_INPUT_NAME_PREFIX}${pascalCase(data.fieldName)}`;
    case 'ranged':
      if (!data.fieldName) {
        throw new Error('search & filters missing data.fieldName');
      }
      return `${WORKFLOW_GENERATED_INPUT_NAME_PREFIX}${pascalCase(data.fieldName)}Ranged`;
    case 'parameter':
      if (!data.fieldName) {
        return;
      }
      return `${WORKFLOW_GENERATED_INPUT_PARAMETER_NAME_PREFIX}${pascalCase(data.fieldName)}`;
    case 'confirm':
      return `${WORKFLOW_GENERATED_INPUT_PARAMETER_NAME_PREFIX}ConfirmButton`;
    default:
      throw new Error(`Unknown field type: ${data.filterType}`);
  }
};

export const isWorkflowInputGenerated = (inputName: string): boolean => {
  // List of prefixes which are considered as generated
  const generatedPrefixes = [
    WORKFLOW_GENERATED_INPUT_NAME_PREFIX,
    WORKFLOW_GENERATED_INPUT_PARAMETER_NAME_PREFIX,
    WORKFLOW_GENERATED_DATA_FILTER_INPUT_NAME_PREFIX,
  ];

  // The input name has to be the prefix + something more
  return generatedPrefixes.some(
    (prefix) => inputName.startsWith(prefix) && inputName.length > prefix.length,
  );
};

export function isWorkflowInputNameEditable(
  workflow?: Workflow | null,
  inputName?: string | null,
): boolean {
  const NON_EDITABLE_INPUT_NAMES = new Set(['$$Account', '$$Page', '$$Url', '$$User']);
  if (
    (!workflow && !inputName) ||
    (inputName && NON_EDITABLE_INPUT_NAMES.has(inputName)) ||
    (inputName && isWorkflowInputGenerated(inputName)) ||
    (workflow && isServerOnlyWorkflow(workflow))
  ) {
    return false;
  }

  return true;
}

export const getDataFilterInputName = (field: string): string =>
  `${WORKFLOW_GENERATED_DATA_FILTER_INPUT_NAME_PREFIX}_${field}`;

export interface WrapperConnectionData {
  eventType: RuntimeEventType;
  /** Data type of the column on which the filter operates */
  fieldColumnDataType?: ColumnDataType;
  /** The id of the column if available */
  fieldId?: string;
  /** The name of the column/parameter */
  fieldName?: string;
  /** IDs used to identify the filter field config - can be multiple, due to fallback mechanisms */
  fieldRefIds: WrapperRefIds;
  filterDataValueField?: string;
  filterOperator?: ComparisonOperator;
  filterType: WrapperFilterType;
  predefinedFilterOperators: PredefinedOperator[];
  searchInFields?: string[];
  sourceElementId: CanvasElementId;
}
